/**
 * The branch switch: which scenarios' numbers every chart is drawing.
 *
 * It sits in the app header rather than on a chart because it is the only
 * control that moves all four at once — every other parameter belongs to the
 * one thing it affects.
 *
 * Branches toggle rather than switch, so more than one can be on at a time. One
 * selected is the plain view of that branch; two is an overlay, where every
 * chart draws both, every rail reports both and the selection pane splits in
 * two. The last selected branch is sticky: there has to be something to draw.
 *
 * `single` is the prose pages' mode, and there it is a radio group instead.
 * Those pages are written *about* a branch — the equations, the tables and the
 * paragraphs are all one branch's — so there is no second column for an overlay
 * to go in, and the honest control is one that picks rather than accumulates.
 */

import {
  LIVE,
  SCIENCE,
  TESTING_3V3,
  formatCompact,
  formatEvo,
  mutagenToReachEvo,
  threatIncomePerMinute,
  feedMutagen,
  type Ruleset,
} from '../model';
import { BranchMark } from './ChartFrame';
import { Info } from './Info';
import { useLab } from './LabContext';

/** The evolutions the comparison table is quoted at. */
const COMPARE_AT = [0.5, 1, 2, 3.3];

/** Units the price table quotes, in the order they appear in a game. */
const PRICED_UNITS = ['small-biter', 'medium-biter', 'big-biter', 'behemoth-biter'];

interface CompareRow {
  evo: number;
  mutagen: number;
  whiteFlasks: number;
  passivePerMinute: number;
}

/** Both branches at a handful of evolutions. Static, so computed once. */
function compare(ruleset: Ruleset): CompareRow[] {
  return COMPARE_AT.map((evo) => {
    const mutagen = mutagenToReachEvo(ruleset, evo, 0);
    return {
      evo,
      mutagen,
      whiteFlasks: mutagen / SCIENCE.space.value,
      passivePerMinute: threatIncomePerMinute(feedMutagen(ruleset, 0, mutagen, 0)),
    };
  });
}

/**
 * Live against the 3v3 test, precomputed once. Deliberately not keyed by
 * `RulesetId`: the tuning scenario has no column here because it is not a fixed
 * set of numbers to tabulate — its own figures are on the controls that set it.
 */
const COMPARISON: Record<'live' | 'testing3v3', CompareRow[]> = {
  live: compare(LIVE),
  testing3v3: compare(TESTING_3V3),
};

/**
 * Cumulative live instant threat at 100% evolution with six players — the one
 * figure the table above cannot carry, since the test branch has no column for
 * it.
 */
const LIVE_INSTANT_THREAT_AT_100 = feedMutagen(
  LIVE,
  0,
  mutagenToReachEvo(LIVE, 1, 0),
  6,
).threatIncrease;

export function BranchSwitch({ single = false }: { single?: boolean }) {
  const {
    ruleset,
    rulesetIds,
    views,
    comparing,
    toggleRulesetId,
    setRulesetIds,
    availableRulesetIds,
    rulesetFor,
  } = useLab();

  return (
    <div className={`branch-switch${single ? ' single' : ''}`}>
      <span className="branch-label">Branch</span>
      <div
        className="segmented"
        role={single ? 'radiogroup' : 'group'}
        aria-label="Branches"
      >
        {availableRulesetIds.map((id) => {
          const entry = rulesetFor(id);
          const selected = rulesetIds.includes(id);
          const view = views.find((candidate) => candidate.ruleset.id === id);
          return (
            <button
              key={id}
              type="button"
              role={single ? 'radio' : undefined}
              className={selected ? 'active' : undefined}
              // Toggles, so pressed state is the honest role — except in single
              // mode, where it really is a radio.
              aria-pressed={single ? undefined : selected}
              aria-checked={single ? selected : undefined}
              // The last one cannot be turned off; saying so beats a click
              // that appears to do nothing. In single mode re-picking the
              // current branch is a harmless no-op, so nothing is disabled.
              disabled={!single && selected && rulesetIds.length === 1}
              title={
                !single && selected && rulesetIds.length === 1
                  ? `${entry.branch} — keep at least one branch selected`
                  : `${entry.branch} — ${entry.tagline}`
              }
              onClick={() => (single ? setRulesetIds([id]) : toggleRulesetId(id))}
            >
              {comparing && view ? <BranchMark dash={view.dash} /> : null}
              {entry.label}
            </button>
          );
        })}
      </div>
      <span
        className="branch-tagline"
        title={comparing ? 'Comparing both branches' : `${ruleset.origin} · ${ruleset.branch}`}
      >
        {comparing ? 'comparing' : <code>{ruleset.branch}</code>}
      </span>
      <Info title="Branch" align="right">
        <p>
          Which scenarios' numbers the four charts are drawing. <b>Live</b> is <code>master</code>,
          what the public servers run. <b>3v3 test</b> is{' '}
          <code>cojito_3v3_balance_testing</code>, ported from the same three files the live
          numbers come from. <b>Scenario</b> is not a transcription of anything — it is the
          candidate being tuned, the shape of <code>feeding_params.lua</code> with every constant
          on a control.
        </p>
        <p>
          <b>Select both to compare.</b> Every chart then draws both branches over the same mutagen
          axis — the same science sent, and what each branch makes of it. Colour still means which
          quantity; the branch is the <b>stroke</b>, solid for the first and dashed for the second.
          Each chart's right rail reports both, and the selection pane splits into one pane per
          branch.
        </p>
        <p>
          On the two <b>prose pages</b> this is a single choice rather than a set. Those pages are
          written about one branch — its equations, its tables, its paragraphs — so there is
          nowhere for a second one to be drawn, and the Scenario is always offered there.
        </p>
        <p>
          The <b>first</b> selected branch owns everything there can only be one of: the zoom
          ceiling, the evolution axis along the top, and the milestone guides. So those read as
          that branch's, while the plotted lines are everyone's.
        </p>
        <p>
          Changing which branch is first holds the <b>evolution</b> window, not the mutagen window.
          The two charge very different amounts of mutagen for the same evolution, so freezing the
          x domain would jump you to a different part of the game and the whole view would look
          like noise. Holding evolution makes it an A/B of the same stretch of a game, and your
          selection comes across the same way. Merely adding a second branch moves nothing.
        </p>

        <p>
          <b>What the test branch changes.</b>
        </p>
        <ul>
          <li>
            The feeding loop is gone. Evolution is a closed form in the total mutagen ever fed:{' '}
            <code>E = (M / 276.66) ^ 0.3</code>. It is cheaper than live below ~90% and steadily
            dearer above it — the live curve freezes at a flat 16.5 mutagen per evolution point
            past 100%, and this one never stops charging more.
          </li>
          <li>
            <b>Instant threat is removed</b> — <code>threat_increase = 0</code> for every send. The
            crowd modifier <code>min(125 + 6.25P, 250)</code> went with it, so the connected-player
            count no longer enters any equation.
          </li>
          <li>
            Passive income is no longer <code>25 × E</code> per second. It is{' '}
            <code>1.5 × M</code> — linear in the <i>total mutagen fed</i>, so each flask is worth
            the same income forever instead of flattening off with evolution. Threat becomes the
            whole economy rather than a race alongside it.
          </li>
          <li>
            Unit prices are renormalised by <code>small = 15</code>, which puts one small biter at
            exactly 1 threat. A behemoth goes from 38.5 to 200, so threat buys far fewer of the
            things that matter — the income numbers below are not comparable to live at face value.
          </li>
          <li>
            Outside the loop, it also retunes the flamethrower: ammo{' '}
            <code>−0.6 → −0.72</code>, turret <code>−0.8 → −0.68</code>. The lab does not chart
            player weapons, so nothing here reflects it.
          </li>
        </ul>

        <table className="branch-table">
          <thead>
            <tr>
              <th>evo</th>
              <th colSpan={2}>white flasks to reach</th>
              <th colSpan={2}>passive threat / min</th>
            </tr>
            <tr>
              <th />
              <th>live</th>
              <th>3v3</th>
              <th>live</th>
              <th>3v3</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE_AT.map((evo, index) => (
              <tr key={evo}>
                <td>{formatEvo(evo, 0)}</td>
                <td>{formatCompact(COMPARISON.live[index].whiteFlasks, 1)}</td>
                <td>{formatCompact(COMPARISON.testing3v3[index].whiteFlasks, 1)}</td>
                <td>{formatCompact(COMPARISON.live[index].passivePerMinute, 1)}</td>
                <td>{formatCompact(COMPARISON.testing3v3[index].passivePerMinute, 1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="branch-table-note">
          White flasks at Normal difficulty, from evolution 0. Live spends most of its threat
          budget on instant threat, which this table does not show at all — at 100% evolution with
          six players that is another {formatCompact(LIVE_INSTANT_THREAT_AT_100, 1)} threat
          delivered on top of the income.
        </p>

        <p>
          <b>What one point of threat buys</b>, in each branch:
        </p>
        <table className="branch-table">
          <thead>
            <tr>
              <th>unit</th>
              <th>live</th>
              <th>3v3</th>
            </tr>
          </thead>
          <tbody>
            {PRICED_UNITS.map((unit) => (
              <tr key={unit}>
                <td>{unit.replace('-', ' ')}</td>
                <td>{LIVE.threatValues[unit]}</td>
                <td>{round2(TESTING_3V3.threatValues[unit])}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p>
          The selection is kept in the address bar as <code>?branch=</code> — comma-separated when
          comparing — so a view can be linked to.
        </p>
      </Info>
    </div>
  );
}

const round2 = (value: number) => Math.round(value * 100) / 100;
